import { useTranslation } from 'next-i18next';
import { EnterTracking } from '@/components/EnterTracking';
import { useViewport } from '@/hooks';
import { FeaturedPeriods } from '@/module/featured/featuedQuiz.const';
import { useCreateSubjectNameGetter } from '@/shared/category';
import { useCachedCategoryInfo, useFetchCategoryByWithAuthUser } from '@/shared/category/useFetchCategoryInfo';
import { AllSchoolType } from '@/shared/category/util';
import { InfiniteListView } from '@/shared/components/itemList/InfiniteListView';
import { filterExternalPlatformQuizzes, useExternalPlatform } from '@/shared/externalPlatform';
import { QuizListItem } from '@/shared/quizList';
import { DropdownSchool } from '@/shared/quizList/DropdownSchool/DropdownSchool';
import { EmptyQuizList } from '@/shared/quizList/EmptyQuizList';
import { useGetQuizListItemButtons } from '@/shared/quizList/hooks';
import { ListLayoutContainer, ListLayoutWrapper } from '@/shared/quizList/Layout';
import { cn } from '@/utils/tailwind.util';
import { useFetchQuizsetRankings } from './hook/useFetchQuizsetRankings';
import { type QuizsetRankingsSchoolType, useSelectedFeaturedQuiz } from './useSelectedFeaturedQuiz';
export const FeaturedQuizPageView = () => {
  useFetchCategoryByWithAuthUser();
  return <ListLayoutWrapper data-sentry-element="ListLayoutWrapper" data-sentry-component="FeaturedQuizPageView" data-sentry-source-file="FeaturedQuizPageView.tsx">
      <ListLayoutContainer data-sentry-element="ListLayoutContainer" data-sentry-source-file="FeaturedQuizPageView.tsx">
        <Header data-sentry-element="Header" data-sentry-source-file="FeaturedQuizPageView.tsx" />
        <div className={'w-full'}>
          <Content data-sentry-element="Content" data-sentry-source-file="FeaturedQuizPageView.tsx" />
        </div>
      </ListLayoutContainer>
    </ListLayoutWrapper>;
};
const Content = () => {
  const {
    categoryTreeData
  } = useCachedCategoryInfo();
  const {
    data,
    isLoading,
    isError
  } = useFetchQuizsetRankings();
  const {
    mode
  } = useExternalPlatform();
  const subjectNameGetter = useCreateSubjectNameGetter(categoryTreeData?.subjectTree);
  const getQuizButtons = useGetQuizListItemButtons('featured');
  const quzListData = data?.data || [];
  const visibleQuizzes = filterExternalPlatformQuizzes(quzListData, mode);
  const {
    selectedPeriod,
    selectedSchoolType
  } = useSelectedFeaturedQuiz();
  return <InfiniteListView items={visibleQuizzes} fetchNextPage={() => {}} isLoading={isLoading} isError={isError} hasNextPage={false} EmptyComponent={<EmptyQuizList type={'featured'} />} renderItem={quiz => <QuizListItem key={quiz.id} quizSet={quiz} menuItems={getQuizButtons(quiz)} subjectName={subjectNameGetter({
    subjectGroupId: quiz.categoryInfo?.subject,
    subSubjectGroupId: quiz.categoryInfo?.subSubject
  })} eventLocation={'featured'} isHideAuthor={false} alwaysDropDownMenu={false} eventAdditionalProperty={{
    featured: {
      location: 'featured',
      period: selectedPeriod,
      schoolType: selectedSchoolType
    }
  }} />} data-sentry-element="InfiniteListView" data-sentry-component="Content" data-sentry-source-file="FeaturedQuizPageView.tsx" />;
};
const Header = () => {
  return <div className={'flex w-full flex-col gap-md max-mobile-lg:gap-xs'} data-sentry-component="Header" data-sentry-source-file="FeaturedQuizPageView.tsx">
      <HeaderTop data-sentry-element="HeaderTop" data-sentry-source-file="FeaturedQuizPageView.tsx" />
      <HeaderBottom data-sentry-element="HeaderBottom" data-sentry-source-file="FeaturedQuizPageView.tsx" />
    </div>;
};
const HeaderTop = () => {
  const {
    selectedSchoolType,
    setSelectedSchoolType
  } = useSelectedFeaturedQuiz();
  const {
    categoryTreeData
  } = useCachedCategoryInfo();
  const schoolTypes = [{
    name: AllSchoolType.name,
    value: AllSchoolType.value
  }, ...(categoryTreeData?.schoolInfoTree.children ?? []).map(d => ({
    name: d.name,
    value: d.name
  }))];
  const {
    isMobile
  } = useViewport();
  const {
    t
  } = useTranslation(['common']);
  return <div className={'flex items-center gap-xs pb-sm pt-xs'} data-sentry-component="HeaderTop" data-sentry-source-file="FeaturedQuizPageView.tsx">
      <DropdownSchool selectedSchoolTypeName={selectedSchoolType} onSelectedSchoolType={v => setSelectedSchoolType(v as QuizsetRankingsSchoolType)} schoolTypes={schoolTypes} data-sentry-element="DropdownSchool" data-sentry-source-file="FeaturedQuizPageView.tsx" />
      <span className={'text-heading-xl-extrabold'}>
        {isMobile ? t('category.popular') : t('common:featured.recommendFeaturedQuiz')}
      </span>
      <span className={'text-heading-xl-extrabold '}></span>
    </div>;
};
const HeaderBottom = () => {
  const {
    setSelectedPeriod,
    selectedPeriod,
    selectedSchoolType
  } = useSelectedFeaturedQuiz();
  const {
    t
  } = useTranslation(['common']);
  return <EnterTracking eventName={'enter_featured_page'} eventProperties={{
    schoolType: selectedSchoolType,
    period: selectedPeriod
  }} data-sentry-element="EnterTracking" data-sentry-component="HeaderBottom" data-sentry-source-file="FeaturedQuizPageView.tsx">
      <div className={'flex w-full justify-between'}>
        <div className={'flex h-[40px] w-fit gap-md'}>
          {FeaturedPeriods.map(period => <div className={cn('flex h-full box-border items-center px-4xs cursor-pointer py-2xs text-heading-md-bold', period.value === selectedPeriod ? 'border-b-[1px] border-[#5D5E69] text-default' : 'border-b-[1px] border-transparent text-assistive')} key={period.value} onClick={() => {
          void setSelectedPeriod(period.value);
          period.track({
            schoolType: selectedSchoolType,
            location: 'featured'
          });
        }}>
              {t(period.name)}
            </div>)}
        </div>
        <div className={'flex  h-[40px] w-fit items-center p-sm text-body-md-semibold text-assistive'}>
          {t('common:featured.shownMaxQuiz')}
        </div>
      </div>
    </EnterTracking>;
};